/**
 * A7Y-5290 — short tracked links on our own domain.
 *
 * `www.apptonomy.ai/go/<word>` forwards to one of our pages with three UTM
 * tags attached, so a LinkedIn comment can carry a short link and Mixpanel
 * still counts the visit exactly as it counts the long tagged URL (the browser
 * lands on that same URL). The admin creates the links; the webapp worker
 * resolves them; a7y-api stores them in `ShortLinks/{word}`.
 *
 * Every rule about what a word, a destination and a tag may be lives here so
 * the API (the authority) and the admin form (instant feedback) apply the same
 * one. Pure and dependency-free: the webapp worker imports it through the
 * `@a7y/types/short-links` subpath.
 */

/** Every short link lives under this one prefix, so no page can ever be shadowed by a link. */
export const SHORT_LINK_PATH_PREFIX = '/go/';

/** The page a link opens when none is chosen: the home page, whose hero runs the quick audit. */
export const SHORT_LINK_DEFAULT_DESTINATION = '/';

/** 1–40 characters of lowercase letters, digits and inner hyphens. */
export const SHORT_LINK_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

/**
 * Words that are the first path segment of a page on www.apptonomy.ai. A link
 * named after a page (`/go/pricing`) would read as that page, so the admin
 * refuses it. `a7y-webapp/worker/short-links-reserved.test.ts` fails when a
 * page's first segment is missing here — the route registry, `SEO_METADATA`,
 * the app-shell families, the worker's own routes and the content site's
 * routes are all read there, because a7y-api cannot import the webapp.
 */
export const SHORT_LINK_RESERVED_SLUGS: ReadonlySet<string> = new Set([
  // the short-link prefix itself
  'go',
  // app shell and auth
  'app',
  'scan',
  'logout',
  'app-shell',
  'signup',
  'sign-in',
  // client-only marketing pages
  'ttest',
  'check-my-app',
  'schedule-demo',
  'unsubscribe',
  'email-preferences',
  'email-cadence',
  'affiliates',
  // prerendered marketing pages and merged-page redirects (A7Y-5446)
  'about',
  'ai-discovery',
  'appcheck',
  'contact',
  'contact-sales',
  'continuous-monitoring',
  'cookie-policy',
  'diy',
  'diy-llm-vs-apptonomy',
  'dpa',
  'engines',
  'faq',
  'features',
  'for',
  'global',
  'how-it-works',
  'methodology',
  'pricing',
  'privacy',
  'refund-policy',
  'release-notes',
  'resources',
  'roadmap',
  // A7Y-5394: the ASO on Rails landing page
  'start',
  'subprocessors',
  'terms',
  'vs',
  // worker routes and redirects
  'security',
  'security-policy',
  'security-advisories',
  'vnext',
  // the content site (a7y-content) on the same host
  'blog',
  'docs',
]);

/** Allowed characters for a UTM tag value. */
export const SHORT_LINK_TAG_RE = /^[A-Za-z0-9._~-]{1,100}$/;

/** A same-site path: `/`-rooted, no `//`, no query or fragment. */
const DESTINATION_RE = /^\/[A-Za-z0-9._~/-]*$/;
const DESTINATION_MAX_LENGTH = 200;

/**
 * The Mixpanel events that mean "a quick audit was started", one per producer:
 * `audit_started` from `/scan` (the home page hero submits there),
 * `Quick Audit Started` from `/appcheck`, and `lead_audit_submitted` with
 * `funnel: "check_my_app"` from `/check-my-app`. `/scan` also emits
 * `lead_audit_submitted` (`funnel: "scan"`) beside `audit_started`, so only the
 * check-my-app funnel's is counted — see {@link isShortLinkQuickAuditEvent}.
 */
export const SHORT_LINK_QUICK_AUDIT_EVENTS = [
  'audit_started',
  'Quick Audit Started',
  'lead_audit_submitted',
] as const;

/** Whether an exported event is one quick-audit start, counted once per audit. */
export function isShortLinkQuickAuditEvent(event: string, properties: { funnel?: unknown }): boolean {
  if (event === 'audit_started' || event === 'Quick Audit Started') return true;
  return event === 'lead_audit_submitted' && properties.funnel === 'check_my_app';
}

export type ShortLinkValidation = { ok: true } | { ok: false; reason: string };

/** Whether `slug` may name a new short link. */
export function validateShortLinkSlug(slug: unknown): ShortLinkValidation {
  if (typeof slug !== 'string' || slug.length === 0) {
    return { ok: false, reason: 'Enter a short word.' };
  }
  if (!SHORT_LINK_SLUG_RE.test(slug)) {
    return {
      ok: false,
      reason:
        'Use 1–40 lowercase letters, digits or hyphens, starting and ending with a letter or digit.',
    };
  }
  if (SHORT_LINK_RESERVED_SLUGS.has(slug)) {
    return { ok: false, reason: `"${slug}" is the name of a page on the site. Choose another word.` };
  }
  return { ok: true };
}

/** Whether `path` may be the page a short link opens. */
export function validateShortLinkDestination(path: unknown): ShortLinkValidation {
  if (typeof path !== 'string' || path.length === 0) {
    return { ok: false, reason: 'Enter the page the link opens, starting with /.' };
  }
  if (path.length > DESTINATION_MAX_LENGTH || !DESTINATION_RE.test(path) || path.includes('//')) {
    return {
      ok: false,
      reason: 'The page must be a path on our site, such as / or /pricing, with no ? or #.',
    };
  }
  return { ok: true };
}

/** Whether `value` may be a source, medium or campaign tag. */
export function validateShortLinkTag(value: unknown, label: string): ShortLinkValidation {
  if (typeof value !== 'string' || value.length === 0) {
    return { ok: false, reason: `Enter the ${label}.` };
  }
  if (!SHORT_LINK_TAG_RE.test(value)) {
    return {
      ok: false,
      reason: `The ${label} may use letters, digits, dots, hyphens and underscores only.`,
    };
  }
  return { ok: true };
}

export interface ShortLinkTags {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
}

/** The path and query a short link forwards to, e.g. `/?utm_source=linkedin&utm_medium=social&utm_campaign=teardown-cal-ai`. */
export function buildShortLinkTargetPath(destinationPath: string, tags: ShortLinkTags): string {
  const query = new URLSearchParams({
    utm_source: tags.utmSource,
    utm_medium: tags.utmMedium,
    utm_campaign: tags.utmCampaign,
  });
  return `${destinationPath}?${query.toString()}`;
}

/** `ShortLinks/{slug}`. */
export interface ShortLinkDoc extends ShortLinkTags {
  slug: string;
  destinationPath: string;
  createdAt: string;
  createdBy: { id: string; email: string };
}

export interface ShortLinkCreateRequest extends ShortLinkTags {
  slug: string;
  destinationPath?: string;
}

/** A link as the admin lists it: the stored document plus the two URLs it stands for. */
export interface AdminShortLink extends ShortLinkDoc {
  shortUrl: string;
  targetUrl: string;
}

export interface AdminShortLinkListResponse {
  links: AdminShortLink[];
}

export interface ShortLinkCounts {
  /** Distinct visitors whose landing carried the link's three tags, at or after the link was created. */
  visitors: number;
  /** Quick audits those visitors started after landing. */
  quickAudits: number;
  /** Those visitors who then signed up. */
  signups: number;
}

export type AdminShortLinkStatsResponse =
  | {
      state: 'ok';
      /** Inclusive UTC dates the Mixpanel export covered. */
      window: { from: string; to: string };
      /** Keyed by slug. */
      counts: Record<string, ShortLinkCounts>;
      computedAt: string;
    }
  | { state: 'unavailable'; reason: string };

/** `GET /:productId/short-links/resolve?word=` — what the webapp worker forwards to. */
export interface ShortLinkResolveResponse {
  target: string | null;
}
